import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { fetchFilesInFolder, type FilesystemResponse, fetchFileList } from '../api/fileApi'
import { type FileInfo } from '../../../utils/types'
import { type PaginatedResponse } from '../../../api'
import { type GenericFilterType, type PaginatedQueryParameters } from '../../../api/hooks/utils/types'
import { type PaginationOptions } from '../../../api/pagination'
// Export search hooks
export { useArticleFileSearch } from './useArticleFileSearch'
export { useRequestFileSearch } from './useRequestFileSearch'
export { useOfferFileSearch } from './useOfferFileSearch'
export { useContractFileSearch } from './useContractFileSearch'
export { useBillFileSearch } from './useBillFileSearch'
export { useGuestRequestFileSearch } from './useGuestRequestFileSearch'
// Re-export search filter types
export type { ArticleFileSearchFilters } from './useArticleFileSearch'
export type { RequestFileSearchFilters } from './useRequestFileSearch'
export type { OfferFileSearchFilters } from './useOfferFileSearch'
export type { ContractFileSearchFilters } from './useContractFileSearch'
export type { BillFileSearchFilters } from './useBillFileSearch'
export type { GuestRequestFileSearchFilters } from './useGuestRequestFileSearch'

/**
 * Hook to fetch files in a folder
 */
export function useFilesInFolder(
  path = '',
  enabled = true
): UseQueryResult<FilesystemResponse, Error> {
  return useQuery({
    queryKey: ['filesystem', 'folder', path],
    queryFn: () => fetchFilesInFolder(path),
    enabled,
  })
}

/**
 * Hook to fetch file list from a specific URL (supports pagination and search)
 * 
 * Note: If filters contain a 'q' parameter, this will automatically use
 * Elasticsearch search APIs. Otherwise, it uses regular list APIs.
 */
export function useFileList(
  url: string | undefined,
  options: PaginatedQueryParameters<FileInfo, GenericFilterType> | PaginationOptions = {},
  enabled = true
): UseQueryResult<PaginatedResponse<FileInfo>, Error> {
  return useQuery({
    queryKey: ['uploadedFiles', url ?? 'local', options.page, options.pageSize, options],
    queryFn: () => {
      if (!url) {
        throw new Error('URL is required for useFileList')
      }
      return fetchFileList(url, options)
    },
    enabled: enabled && !!url,
  })
}

